import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Clock, DollarSign, CalendarDays, Receipt, MapPin, Bell, AlertTriangle, FileText } from "lucide-react";
import { useMyWorkerRecord, useMyStats, useMyReimbursements, useMyBills, useMyNotifications } from "@/hooks/useWorkerPortal";
import { format } from "date-fns";
import { useTabNavigate } from "@/hooks/useTabNavigate";

const fmt = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const WorkerDashboard = () => {
  const { data: worker, isLoading } = useMyWorkerRecord();
  const stats = useMyStats(worker?.id);
  const { data: reimbursements = [] } = useMyReimbursements(worker?.id);
  const { data: bills = [] } = useMyBills(worker?.vendor_id || undefined);
  const { data: notifications = [] } = useMyNotifications(5);
  const navigate = useTabNavigate();

  const pendingReimbursements = reimbursements.filter((r: any) => r.status === "pending");
  const voidedBills = bills.filter((b: any) => b.status === "voided");
  const pendingBills = bills.filter((b: any) => b.status === "pending" || b.status === "draft");
  const firstName = worker?.first_name || worker?.worker_name?.split(" ")[0] || "Worker";
  const initials = `${(worker?.first_name || "W")[0]}${(worker?.last_name || "P")[0]}`.toUpperCase();
  const project = worker?.personnel_project as any;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-16 w-64" />
        <div className="grid grid-cols-3 gap-4">
          <Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-28" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div className="flex items-center gap-4">
        <Avatar className="h-14 w-14">
          <AvatarImage src={worker?.profile_photo_url || undefined} />
          <AvatarFallback className="text-lg bg-primary text-primary-foreground">{initials}</AvatarFallback>
        </Avatar>
        <div>
          <h1 className="text-2xl font-bold">Good morning, {firstName}!</h1>
          {project && <p className="text-muted-foreground">{project.name}</p>}
        </div>
      </div>

      {/* Voided Bills Alert */}
      {voidedBills.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <p className="font-semibold">
              {voidedBills.length} bill{voidedBills.length > 1 ? "s" : ""} voided
            </p>
            {voidedBills.slice(0, 2).map((b: any) => (
              <p key={b.id} className="text-sm mt-1">
                Bill #{b.bill_number}: {(b as any).void_reason || "No reason provided"}
              </p>
            ))}
            <Button
              size="sm"
              variant="destructive"
              className="mt-2"
              onClick={() => navigate("/portal/worker/payments")}
            >
              View Details
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* At-a-Glance Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate("/portal/worker/payments")}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <FileText className="h-4 w-4" /> Pending Bills
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{pendingBills.length}</p>
            <p className="text-sm text-muted-foreground">
              {pendingBills.length === 0 ? "All clear" : `${fmt(pendingBills.reduce((s: number, b: any) => s + (b.total || 0), 0))} awaiting review`}
            </p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => navigate("/portal/worker/reimbursements")}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Receipt className="h-4 w-4" /> Pending Reimbursements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{pendingReimbursements.length}</p>
            <p className="text-sm text-muted-foreground">
              {pendingReimbursements.length === 0 ? "None pending" : `${fmt(pendingReimbursements.reduce((s: number, r: any) => s + (r.amount || 0), 0))} submitted`}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4" /> YTD Earnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{fmt(stats.ytdEarned)}</p>
            <p className="text-sm text-muted-foreground">{stats.ytdHours.toFixed(1)} hours worked</p>
          </CardContent>
        </Card>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" /> This Week
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.weekHours.toFixed(1)} hrs</p>
            <p className="text-sm text-muted-foreground">{fmt(stats.weekEarned)} earned</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CalendarDays className="h-4 w-4" /> This Month
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.monthHours.toFixed(1)} hrs</p>
            <p className="text-sm text-muted-foreground">{fmt(stats.monthEarned)} earned</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4" /> YTD
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.ytdHours.toFixed(1)} hrs</p>
            <p className="text-sm text-muted-foreground">{fmt(stats.ytdEarned)} earned</p>
          </CardContent>
        </Card>
      </div>

      {/* Active Projects */}
      <Card>
        <CardHeader><CardTitle className="text-base">Active Projects</CardTitle></CardHeader>
        <CardContent>
          {project ? (
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 mt-1 text-muted-foreground" />
              <div>
                <p className="font-medium">{project.name}</p>
                <p className="text-sm text-muted-foreground">
                  {project.project_number} {project.location_city && `${project.location_city}, ${project.location_state}`}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-[10px]">
                    {worker?.rate_schedule?.role_name || "Worker"} @ {fmt(worker?.rate_schedule?.bill_rate_regular || 0)}/HR
                  </Badge>
                  {worker?.start_date && (
                    <span className="text-xs text-muted-foreground">Since {format(new Date(worker.start_date), "MMM d, yyyy")}</span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No active project assignments yet.</p>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="h-4 w-4" /> Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {notifications.length === 0 ? (
            <p className="text-sm text-muted-foreground">No recent activity.</p>
          ) : (
            <div className="space-y-3">
              {notifications.map((n: any) => (
                <div key={n.id} className="flex items-start gap-3 text-sm">
                  <div className={`mt-1 h-2 w-2 rounded-full shrink-0 ${n.status === "unread" ? "bg-primary" : "bg-muted-foreground/30"}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium ${n.status === "unread" ? "" : "text-muted-foreground"}`}>
                      {n.title}
                    </p>
                    {n.message && (
                      <p className="text-muted-foreground text-xs truncate">{n.message}</p>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {n.created_at ? format(new Date(n.created_at), "MMM d") : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Reimbursements */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Pending Reimbursements</CardTitle>
          <Button size="sm" variant="outline" onClick={() => navigate("/portal/worker/reimbursements")}>
            <Receipt className="h-4 w-4 mr-1" /> Submit Receipt
          </Button>
        </CardHeader>
        <CardContent>
          {pendingReimbursements.length === 0 ? (
            <p className="text-sm text-muted-foreground">No pending reimbursements.</p>
          ) : (
            <div className="space-y-2">
              {pendingReimbursements.map((r: any) => (
                <div key={r.id} className="flex items-center justify-between text-sm">
                  <span>{r.description} -- {r.submitted_at ? format(new Date(r.submitted_at), "MMM d") : "--"}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{fmt(r.amount)}</span>
                    <Badge variant="secondary" className="text-[10px]">Pending</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WorkerDashboard;
